import React, { Component } from "react";
import ReactPaginate from 'react-paginate';
import Product from './product-item.jsx';
import LoaderSection from './loader-section.jsx';
import { getProductFilterOptions, getFilteredProductsByClassification } from '../../apis/filters';
import { getUrlParams, generatePaginationHref, toClassName, isMetricGuess } from '../../utils/';
import debounce from 'lodash/debounce';
import Filters from './filters.jsx';
import toast from '../../components/toast';
import FilteringBase from './filtering-base';

class GenusProductsContainer extends FilteringBase {
    constructor(props) {
        super(props);

        this.state = Object.assign({}, this.baseState, {
            filters: [],
            isLoadingFilters: false,
            firstSearch: true
        });

        this.onSearchInput = this.onSearchInput.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handlePageClick = this.handlePageClick.bind(this);
        this.loadDebounce = debounce(this.load.bind(this), 1000);
    }

    componentDidMount() {
        // get filters
        this.loadFilters()
            .then(data => {
                console.log(data);
                let params = getUrlParams();
                this.updateFilters(params);
                // reload filters if any are set
                if (Object.keys(params).length) {
                    this.loadFilters()
                        .then(filters => {
                            console.log(filters);
                            this.loadFiltersAndProductsOnMount(params);
                        })
                        .catch(error => {
                            console.log(error);
                            this.setState({
                                isLoading: false
                            });
                            toast.error('There was an error getting product filters');
                        });
                }
                else {
                    this.loadFiltersAndProductsOnMount(params);
                }
            })
            .catch(error => {
                console.log(error);
                this.setState({
                    isLoading: false
                });
                toast.error('There was an error getting product filters');
            });
    }

    loadFiltersAndProductsOnMount(params) {
        this.checkParams(Object.keys(params).length, true, params);

        window.addEventListener('popstate', e => {
            let params = getUrlParams();
            this.updateFilters(params);
            console.log(location.pathname + location.search);

            this.checkParams(Object.keys(params).length, false, params);
        });
    }

    checkParams(length, popstate = true, params) {
        if (length) {
            this.setState({ page: this.getPageNum(params) }, () => {
                this.load(popstate);
            });
        } else {
            if (popstate) {
                this.load(popstate);
            } else {
                this.setState({ page: 1 }, () => { this.load(popstate) });
            }
        }
    }

    getPageNum(params) {
        return params.page && params.page !== '1' ? params.page : 1;
    }

    // load products from the api
    load(pushState = true) {
        if (this.ajaxPromise) this.ajaxPromise.controller.abort();

        this.setState({
            isLoading: true,
            items: []
        });

        if (pushState && !this.state.firstSearch) {
            history.pushState(null, document.title, this.getUrl());
        }

        // put together api parameters
        const params = {
            q: this.state.q,
            page: this.state.page,
            pageSize: this.state.pageSize,
            selected: this.getSelectedApiParams()
        };

        // remove empty or undefined params
        Object.keys(params).forEach(key => {
            if (!params[key]) delete params[key];
        });

        this.setState({
            firstSearch: false
        });

        this.ajaxPromise = getFilteredProductsByClassification(this.props.id, params);
        return this.ajaxPromise
            .then(data => {
                console.log(data);
                this.setState({
                    items: data.records,
                    totalRecords: data.totalRecords
                });
                return data;
            })
            .catch(error => {
                console.log(error);
                if (error.name === 'AbortError') return error;
                toast.error('There was an error getting products');
            })
            .finally(() => {
                this.setState({
                    isLoading: false
                });
            });
    }

    loadFilters() {
        if (this.filterPromise) this.filterPromise.controller.abort();

        const params = {
            selected: this.getSelectedApiParams()
        };
        if (!params.selected) delete params['selected'];
        this.setState({
            isLoadingFilters: true
        });

        this.filterPromise = getProductFilterOptions(this.props.id, params);
        return this.filterPromise
            .then(data => {
                console.log(data);
                this.resetFilterData(data);
                // map currently selected filters to new filters
                const selected = [];
                for (const [key, value] of Object.entries(this.state.filters)) {
                    value.forEach(v => {
                        if (v.selected) selected.push(v.filterGuid);
                    });
                }
                for (const [key, value] of Object.entries(data)) {
                    value.forEach(v => {
                        if (selected.indexOf(v.filterGuid) >= 0) v.selected = true;
                    });
                }
                this.setState({
                    filters: data
                });
                return data;
            })
            .catch(error => {
                console.log(error);
                if (error.name === 'AbortError') return;
            })
            .finally(() => {
                this.setState({
                    isLoadingFilters: false
                });
            });
    }

    getSelectedApiParams() {
        const selected = [];
        for (const [key, value] of Object.entries(this.state.filters)) {
            value.forEach(v => {
                if (v.selected) selected.push(v.filterGuid);
            });
        }

        return selected.join(',');
    }

    // set the selected state of the filters to false
    resetFilterData(data) {
        for (const [key, value] of Object.entries(data)) {
            value.forEach(v => v.selected = false);
        }
    }

    // update filters on page using the url parameters
    updateFilters(params) {

        const filters = Object.assign({}, this.state.filters);

        // reset filters to be unselected
        this.resetFilterData(filters);
        // go through params and update selected filters
        for (const paramKey in params) {
            const values = params[paramKey].split('|').join(',').split(',');
            const filterSection = Object.entries(filters).find(([key]) => toClassName(key) === toClassName(paramKey));
            console.log(filterSection, values);

            if (filterSection) {
                values.forEach(value => {
                    const filter = filterSection[1].find(f => toClassName(f.value) === toClassName(value));
                    if (filter) filter.selected = true;
                });
            }
        }

        // update search query
        if (params.q) this.setState({ q: params.q });

        this.setState({
            filters: filters,
            page: params['page'] ? +params['page'] : 1
        });
    }

    // get all the selected filters at this time
    getAllFilters() {
        const params = {
            q: this.state.q,
            page: this.state.page
        };

        for (const [key, value] of Object.entries(this.state.filters)) {
            value.forEach(v => {
                if (v.selected) {
                    const paramKey = toClassName(key),
                        encodedValue = toClassName(v.Value);
                    if (params[paramKey]) params[paramKey].push(encodedValue);
                    else params[paramKey] = [encodedValue];
                }
            });
        }

        // pipe delimit array values
        Object.keys(params).forEach(key => {
            if (Array.isArray(params[key])) params[key] = params[key].join('|');
        });

        return params;
    }

    // get the url for the current window location
    getUrl() {
        const baseUrl = `${window.location.pathname}`,
            filters = this.getAllFilters(),
            params = [];
        for (const key in filters) {
            let val = filters[key];
            if (((key === 'page' && val > 1) || key !== 'page')) {
                if (typeof val === 'string')
                    if (val.indexOf('|') > 0 || val.indexOf(',') > 0)
                        val = val.split('|').join(',').split(',').map(value => toClassName(value)).join('|');
                    else
                        val = toClassName(val);
                if (val) params.push(`${key}=${encodeURIComponent(val)}`);
            }
        }

        return `${baseUrl}${params.length ? '?' : ''}${params.join('&')}`;
    }

    handleChange(e) {
        const target = event.target,
            isChecked = target.checked,
            value = target.value,
            filters = Object.assign({}, this.state.filters);

        Object.keys(filters).forEach(key => {
            const match = filters[key].find(f => f.filterGuid === value);
            if (match) match.selected = isChecked;
        });

        this.setState({
            filters: filters,
            page: 1
        }, () => {
            this.loadFilters();
            this.load();
        });
    }

    onSearchInput(value) {
        this.setState({
            q: value
        }, () => {
            this.loadDebounce();
        });
    }

    shouldShowFilters() {
        return Object.entries(this.state.filters).length > 0;
    }

    render() {
        let pagination,
            loader,
            results;
        if (this.showPagination()) {
            pagination = <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                breakClassName={'break-me'}
                pageCount={this.pages()}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={this.handlePageClick}
                containerClassName={'pagination'}
                subContainerClassName={'pages pagination'}
                activeClassName={'active'}
                forcePage={this.state.page - 1}
                hrefBuilder={generatePaginationHref}
            />;
        }

        if (this.state.items.length > 0) {
            results = <div className="row">
                {this.state.items.map((product, index) => (
                    <Product key={index} showAddToProject={this.props.isSales} {...product} />
                ))}
            </div>;
        }
        else if (!this.state.isLoading) {
            results = <div><p className="no-results">No results found</p></div>;
        }

        if (this.state.isLoading) loader = <LoaderSection show={true} />;

        let filterComponent;
        if (this.shouldShowFilters()) {
            filterComponent = <div className="col-lg-3">
                <Filters q={this.state.q} handleSearchChange={this.onSearchInput}>
                    {Object.entries(this.state.filters).map(([key, value], index) => (
                        <details className="filter-accordion" open={!isMetricGuess(key)} key={key} role="group" aria-labelledby={'filter-' + toClassName(key)}>
                            <summary className="filter-accordion-header" id={'filter-' + toClassName(key)}><span>{key}</span></summary>
                            <div className="filter-accordion-content">
                                {value.map((filter, index) => (
                                    <div key={index}>
                                        <input className="custom-checkbox square" id={filter.filterGuid} type="checkbox" name={key} value={filter.filterGuid} onChange={this.handleChange} checked={filter.selected} disabled={this.state.isLoadingFilters} />
                                        <label htmlFor={filter.filterGuid}>
                                            <span>{filter.value} <span className="dull">({filter.numberOfProducts})</span></span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </details>
                    ))}
                </Filters>
            </div>;
        }

        return (
            <div className={this.shouldShowFilters() ? 'row' : 'row justify-content-center'}>
                {filterComponent}
                <div id="results" className={this.shouldShowFilters() ? 'col-lg-9' : 'col-lg-10'}>
                    {loader}
                    {results}
                    {pagination}
                </div>
            </div>
        );
    }
}

export default GenusProductsContainer;